import React, { useEffect, useMemo, useState } from "react";
import { http } from "../lib/api";
import { applyCandidateFilters, filtersActive, filterSummary } from "../lib/filters";

const STAGES = ["New", "Screening", "Interview", "Offer", "Hired", "Rejected"];
const STATUS_PILL = {
  New: "pill-new",
  Screening: "pill-screening",
  Interview: "pill-interview",
  Offer: "pill-offer",
  Hired: "pill-hired",
  Rejected: "pill-rejected",
};

export default function CandidateStatus({ statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [candidates, setCandidates] = useState([]);

  const load = async () => {
    const r = await http.get("/candidates");
    setCandidates(r.data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    try {
      await http.patch(`/candidates/${id}/status`, { status });
    } catch (_e) {
      load();
    }
  };

  const visibleCandidates = useMemo(
    () => applyCandidateFilters(candidates, statusFilter, locationFilter, requisitionFilter),
    [candidates, statusFilter, locationFilter, requisitionFilter],
  );

  const buckets = STAGES.map((s) => ({
    stage: s,
    items: visibleCandidates.filter((c) => c.status === s),
  }));

  return (
    <div className="space-y-5" data-testid="candidate-status-page">
      <div>
        <div className="label-eyebrow">Pipeline Kanban</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Candidate Status</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Move candidates through the recruitment stages.
        </p>
      </div>

      {filtersActive(statusFilter, locationFilter, requisitionFilter) && (
        <div className="text-xs text-[#002FA7] bg-indigo-50 rounded px-3 py-2 flex items-center justify-between" data-testid="status-filter-active">
          <span>Sidebar filter active — {filterSummary(statusFilter, locationFilter, requisitionFilter)}</span>
          <span className="font-mono">{visibleCandidates.length}/{candidates.length} candidates shown</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kanban-board">
        {buckets.map(({ stage, items }) => (
          <div key={stage} className="card p-3 min-h-[400px]" data-testid={`column-${stage}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`pill ${STATUS_PILL[stage]}`}>{stage}</span>
              <span className="text-xs font-mono text-slate-400">{items.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((c) => (
                <div key={c.id} className="border bg-white p-2.5 rounded-sm" data-testid={`kanban-card-${c.id}`}>
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.position}</div>
                  <div className="text-[10px] text-slate-400 mt-1 font-mono">Score {c.matchScore}</div>
                  <select
                    className="input mt-2 !py-1 !text-[11px]"
                    value={c.status}
                    onChange={(e) => updateStatus(c.id, e.target.value)}
                    data-testid="status-select"
                    data-candidate-id={c.id}
                  >
                    {STAGES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-xs text-slate-300 text-center py-6">Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
